/**
 * Which deployment an event came from.
 *
 * PostHog's recommended way to keep staging and local data out of production
 * numbers is a separate project per environment, which is purely a matter of
 * pointing each deployment at a different `NEXT_PUBLIC_POSTHOG_KEY` — no code
 * branches on this value. (PostHog did ship an "environments" concept *inside*
 * a project and then rolled it back, so projects are the mechanism.)
 *
 * This property exists anyway, for two reasons the separate keys do not cover:
 * it makes a misconfigured deployment visible — events landing in the
 * production project tagged `preview` mean somebody's key is wrong, which is
 * otherwise silent and corrupts the numbers for months — and it keeps the
 * historical data already captured in the shared project filterable.
 *
 * `NEXT_PUBLIC_VERCEL_ENV` is one of production/preview/development and is
 * populated by Vercel when "Automatically expose System Environment Variables"
 * is on; locally it is absent and NODE_ENV answers instead.
 */

export type AnalyticsEnvironment = "production" | "preview" | "development";

export const resolveAnalyticsEnvironment = (): AnalyticsEnvironment => {
  const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV;
  if (vercelEnv === "production" || vercelEnv === "preview" || vercelEnv === "development") {
    return vercelEnv;
  }
  return process.env.NODE_ENV === "production" ? "production" : "development";
};

/**
 * Registered as a super property on the client and merged into every
 * server-side capture, so no call site has to remember it.
 */
export const ANALYTICS_ENVIRONMENT_PROPERTY = "environment";
